import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Users, GraduationCap, Calendar, BookOpen, FileText, Receipt, Download, FileSpreadsheet, TrendingUp, Euro, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Layout from '@/components/layout/Layout';
import { palette } from '@/lib/mamvPalette';
import { formatDeNumber } from '@/lib/utils';
import DateInput from '@/components/ui/date-input';

export default function Reports() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teachers, setTeachers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);
  
  // Filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [locationId, setLocationId] = useState('');
  
  const isTeacher = user?.roles?.some(r => ['teacher', 'lehrer'].includes(r.name)) && 
                   !user?.roles?.some(r => ['admin', 'secretary', 'sekretariat', 'verwaltung'].includes(r.name));

  useEffect(() => {
    loadFilterOptions();
    loadKPIs();
    if (!isTeacher) {
      loadRevenueTrend();
    }
  }, []);

  const loadFilterOptions = async () => {
    try {
      const [teachersRes, locationsRes] = await Promise.all([
        api.get('/reports/teachers'),
        api.get('/reports/locations')
      ]);
      setTeachers(teachersRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error('Failed to load filter options:', error);
    }
  };

  const loadKPIs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from', fromDate);
      if (toDate) params.append('to', toDate);
      if (teacherId) params.append('teacher_id', teacherId);
      if (locationId) params.append('location_id', locationId);
      
      const response = await api.get(`/reports/kpis?${params.toString()}`);
      setKpis(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der KPIs');
    } finally {
      setLoading(false);
    }
  };

  const loadRevenueTrend = async () => {
    try {
      const response = await api.get('/reports/revenue-trend?months=6');
      setRevenueTrend(response.data.months || []);
    } catch (error) {
      console.error('Failed to load revenue trend:', error);
    }
  };

  const handleExport = async (type, format) => {
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('from', fromDate);
      if (toDate) params.append('to', toDate);
      if (teacherId) params.append('teacher_id', teacherId);
      if (locationId) params.append('location_id', locationId);
      
      const endpoint = `/exports/${type}.${format}?${params.toString()}`;
      
      const response = await api.get(endpoint, { responseType: 'blob' });
      
      const filename = {
        students: 'schueler',
        courses: 'kurse',
        enrollments: 'einschreibungen',
        'lesson-events': 'unterrichtseinheiten'
      }[type] || type;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export ${filename}.${format} gestartet`);
    } catch (error) {
      toast.error('Export fehlgeschlagen');
    }
  };

  const KPICard = ({ title, value, icon: Icon, color = "brand", subtitle }) => (
    <Card data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{title}</p>
            <p className="text-3xl font-bold text-slate-900">{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <div className="w-12 h-12 rounded-full bg-brand-red/10 flex items-center justify-center">
            <Icon className="w-6 h-6 text-brand-red" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const ExportButton = ({ type, label }) => (
    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg" data-testid={`export-${type}`}>
      <FileText className="w-5 h-5 text-slate-600" />
      <span className="flex-1 text-sm font-medium text-slate-700">{label}</span>
      <Button
        size="sm"
        variant="outline"
        onClick={() => handleExport(type, 'csv')}
        data-testid={`export-${type}-csv`}
      >
        <Download className="w-4 h-4 mr-1" />
        CSV
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => handleExport(type, 'xlsx')}
        data-testid={`export-${type}-xlsx`}
      >
        <FileSpreadsheet className="w-4 h-4 mr-1" />
        XLSX
      </Button>
    </div>
  );

  return (
    <Layout>
    <div className="p-8 space-y-8" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports & Auswertungen</h1>
          <p className="text-slate-500 mt-1">
            {isTeacher ? 'Übersicht Ihrer Kurse und Schüler' : 'Übersicht der Musikschuldaten'}
          </p>
        </div>
      </div>

      {/* Filter Section */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fromDate">Von</Label>
              <DateInput
                id="fromDate"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                data-testid="filter-from-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="toDate">Bis</Label>
              <DateInput
                id="toDate"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                data-testid="filter-to-date"
              />
            </div>
            {!isTeacher && (
              <div className="space-y-2">
                <Label>Lehrkraft</Label>
                <Select value={teacherId || "all"} onValueChange={(v) => setTeacherId(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-teacher">
                    <SelectValue placeholder="Alle Lehrkräfte" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Lehrkräfte</SelectItem>
                    {teachers.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Standort</Label>
              <Select value={locationId || "all"} onValueChange={(v) => setLocationId(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-location">
                  <SelectValue placeholder="Alle Standorte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Standorte</SelectItem>
                  {locations.map(l => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={loadKPIs} className="w-full" data-testid="apply-filters">
                <TrendingUp className="w-4 h-4 mr-2" />
                Aktualisieren
              </Button>
            </div>
          </div>
          {isTeacher && (
            <p className="text-xs text-brand-red mt-4 flex items-center gap-1">
              <span className="inline-block w-2 h-2 bg-brand-red rounded-full"></span>
              Als Lehrkraft sehen Sie nur Ihre eigenen Daten
            </p>
          )}
        </CardContent>
      </Card>

      {/* KPI Cards */}
      {!loading && kpis && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <KPICard 
              title="Schüler" 
              value={kpis.students_active} 
              icon={Users}
              subtitle={`${kpis.students_total} gesamt`}
            />
            <KPICard 
              title="Lehrkräfte" 
              value={kpis.teachers_total} 
              icon={GraduationCap}
              color="blue"
            />
            <KPICard 
              title="Aktive Kurse" 
              value={kpis.courses_active} 
              icon={BookOpen}
              color="green"
            />
            <KPICard 
              title="Einschreibungen" 
              value={kpis.enrollments_active} 
              icon={FileText}
              color="orange"
            />
            <KPICard 
              title="Geplante Stunden" 
              value={kpis.lessons_scheduled_in_range} 
              icon={Calendar}
              color="pink"
              subtitle={kpis.filters_applied.from_date || kpis.filters_applied.to_date 
                ? `${kpis.filters_applied.from_date || '...'} bis ${kpis.filters_applied.to_date || '...'}` 
                : 'Kein Zeitraum gewählt'}
            />
            {kpis.billing && (
              <KPICard 
                title="Umsatz (bezahlt)" 
                value={`${formatDeNumber(kpis.billing.total_revenue)} €`} 
                icon={Euro}
                color="emerald"
                subtitle={`${kpis.billing.invoices_paid} Rechnungen`}
              />
            )}
          </div>

          {/* Billing KPIs for Admin/Secretary */}
          {kpis.billing && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  Abrechnungsübersicht
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-slate-900">{kpis.billing.invoices_total}</p>
                    <p className="text-sm text-slate-500">Rechnungen gesamt</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-brand-red">{kpis.billing.invoices_open}</p>
                    <p className="text-sm text-slate-500">Offen</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">{kpis.billing.invoices_paid}</p>
                    <p className="text-sm text-slate-500">Bezahlt</p>
                  </div>
                  <div className="p-4 bg-brand-red/5 rounded-lg text-center">
                    <p className="text-2xl font-bold text-brand-red">{kpis.billing.contracts_active}</p>
                    <p className="text-sm text-slate-500">Aktive Verträge</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Revenue Trend Chart - Only for Admin/Secretary */}
          {!isTeacher && revenueTrend.length > 0 && (
            <Card data-testid="revenue-trend-chart">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Einnahmen-Trend (letzte 6 Monate)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={revenueTrend} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        axisLine={{ stroke: '#cbd5e1' }}
                      />
                      <YAxis 
                        tick={{ fill: '#64748b', fontSize: 12 }}
                        axisLine={{ stroke: '#cbd5e1' }}
                        tickFormatter={(value) => `${value}€`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1e293b', 
                          border: 'none', 
                          borderRadius: '8px',
                          color: '#f8fafc'
                        }}
                        formatter={(value, name) => {
                          if (name === 'revenue') return [`${formatDeNumber(value)} €`, 'Einnahmen'];
                          if (name === 'invoices_paid') return [value, 'Bezahlte Rechnungen'];
                          return [value, name];
                        }}
                        labelStyle={{ color: '#94a3b8' }}
                      />
                      <Legend 
                        formatter={(value) => {
                          if (value === 'revenue') return 'Einnahmen (€)';
                          if (value === 'invoices_paid') return 'Bezahlte Rechnungen';
                          return value;
                        }}
                      />
                      <Bar 
                        dataKey="revenue" 
                        fill={palette.primary} 
                        radius={[4, 4, 0, 0]}
                        name="revenue"
                      />
                      <Bar 
                        dataKey="invoices_paid" 
                        fill="#10b981" 
                        radius={[4, 4, 0, 0]}
                        name="invoices_paid"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-slate-400 mt-4 text-center">
                  Zeigt bezahlte Rechnungen und Einnahmen pro Monat
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Datenexport bleibt Verwaltungsrollen vorbehalten. */}
      {!isTeacher && <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Download className="w-5 h-5" />
            Datenexport
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-500 mb-4">
            Exportieren Sie Daten als CSV oder Excel-Datei. Die aktuellen Filter werden angewendet.
          </p>
          <ExportButton type="students" label="Schüler" />
          <ExportButton type="courses" label="Kurse" />
          <ExportButton type="enrollments" label="Einschreibungen" />
          <ExportButton type="lesson-events" label="Unterrichtseinheiten" />
        </CardContent>
      </Card>}

      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-red"></div>
        </div>
      )}
    </div>
    </Layout>
  );
}
